import { Address } from "~/entities/siteEntities";
import { ParsedAddress } from "~/helpers/coordsHelpers";

/**
 * Work out the address to save from the change-order modal.
 *
 * Three inputs, and the whole subtlety is in how they interact:
 *
 *  - `typedAddress` — what is in the input. It is PRE-FILLED from the order's
 *    saved address, so "unchanged" is the common case, not the rare one.
 *  - `parsed` — components of a prediction the customer actively picked from the
 *    Places dropdown. Null unless they picked one *this time round*.
 *  - `saved` — the address already on the order, coordinates included.
 *
 * The bug this exists to prevent: taking coordinates from `parsed` alone meant
 * pressing Save on a pre-filled, unchanged address wrote `latitude: null` and
 * `longitude: null`, discarding coordinates the order already had. PlaceOrder
 * then rejected the order with INVALID_DELIVERY_ADDRESS, and the customer saw
 * "Delivery address must have valid coordinates" for an address they never
 * touched.
 *
 * The rule: a freshly picked prediction always wins. Otherwise fall back to the
 * saved address's own coordinates, but ONLY while the text is unchanged — a new
 * address that was typed and never picked from the dropdown must resolve to no
 * coordinates, because the saved ones belong to the previous address and
 * silently delivering to the wrong place is far worse than an error.
 */
export const resolveDeliveryAddress = (
  typedAddress: string,
  parsed: ParsedAddress | null,
  saved: Address | null | undefined,
): Address => {
  const unchanged =
    !!saved && typedAddress.trim() === (saved.address1 ?? "").trim();
  const fallback = unchanged ? saved : null;

  return {
    // The STREET LINE, not the whole formatted address. The courier-ready string
    // is composed back up from these components server-side
    // (helpers.FormatUberAddress), so storing the street here loses nothing and
    // lets the storefront show "193 Glengarry Road" without guessing at commas.
    //
    // Falls back to the typed text when there are no components to compose from
    // — a legacy saved address, or a value typed and never picked.
    address1: parsed?.street || fallback?.address1 || typedAddress,
    address2: "",
    city: parsed?.city ?? fallback?.city ?? "",
    countryId: parsed?.countryId ?? fallback?.countryId ?? null,
    postalCode: parsed?.postalCode ?? fallback?.postalCode ?? "",
    state: parsed?.state ?? fallback?.state ?? "",
    latitude: parsed?.latitude ?? fallback?.latitude ?? null,
    longitude: parsed?.longitude ?? fallback?.longitude ?? null,
  };
};
